import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchSiteSettings } from "@/lib/content";
import darkLogoAsset from "@/assets/logo-dark.jpg.asset.json";

export function Logo({ className = "", size = "default", variant = "default" }: { className?: string; size?: "default" | "lg"; variant?: "default" | "footer" }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const { data: settings } = useQuery({
    queryKey: ["site_settings"],
    queryFn: fetchSiteSettings,
    staleTime: 5 * 60 * 1000,
    enabled: mounted,
  });

  const lightLogo = mounted ? (settings?.logo_url as string | undefined) : undefined;
  const darkLogoRaw = mounted ? (settings?.logo_url_dark as string | undefined) : undefined;
  // Always prefer the admin-uploaded dark logo; otherwise fall back to the bundled dark-mode
  // asset. The fallback is a constant URL so it renders identically on SSR and first client
  // paint (no hydration mismatch) and guarantees the dark-mode white circle is always visible.
  const darkLogo = darkLogoRaw || darkLogoAsset.url;
  const needsDarkFilter = false;
  // Use defaults on first paint to match SSR exactly (prevents hydration mismatch
  // which forces a full re-render and tanks mobile FCP/LCP/TTI).
  const siteName = mounted ? ((settings?.site_name as string | undefined) || "Online Tax VAT") : "Online Tax VAT";
  const tagline = mounted ? ((settings?.tagline as string | undefined) || "Bangladesh") : "Bangladesh";
  const hasAnyLogo = true;

  useEffect(() => {
    try {
      if (lightLogo) localStorage.setItem("site_logo_url", lightLogo);
      if (siteName) localStorage.setItem("site_name", siteName);
    } catch {}
  }, [lightLogo, siteName]);

  const isLg = size === "lg";
  const isFooter = variant === "footer";
  const sizeClasses = {
    text: "",
    name: isLg
      ? "text-2xl md:text-3xl font-display bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 bg-clip-text text-transparent dark:from-emerald-300 dark:via-teal-200 dark:to-emerald-400"
      : isFooter
        ? "text-2xl md:text-[26px] tracking-[0.08em]"
        : "text-lg md:text-xl tracking-[0.06em]",
    tag: isLg
      ? "text-[11px] md:text-xs mt-0.5"
      : isFooter
        ? "text-[11px] md:text-[12px] -mt-0.5 tracking-[0.135em]"
        : "text-[10px] md:text-[11px] -mt-0.5 tracking-[0.08em] normal-case",
  };

  return (
    <Link to="/" className={`logo-critical flex items-center gap-3 group shrink-0 ${className}`}>
      {hasAnyLogo ? (
        <div
          className="relative h-12 w-12 md:h-14 md:w-14 shrink-0 rounded-full dark:bg-white"
          style={{ contain: "strict", aspectRatio: "1 / 1", width: 56, height: 56 }}
        >
          {lightLogo && (
            <img
              src={lightLogo}
              alt="Online Tax VAT Bangladesh Logo"
              width={56}
              height={56}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              sizes="56px"
              style={{ objectFit: "contain", aspectRatio: "1 / 1" }}
              className="absolute inset-0 h-full w-full rounded-full ring-2 ring-emerald-500/40 shadow-glow transition-transform group-hover:scale-105 block dark:hidden"
            />
          )}
          {darkLogo && (
            <img
              src={darkLogo}
              alt="Online Tax VAT Bangladesh Logo"
              width={56}
              height={56}
              loading="lazy"
              decoding="async"
              sizes="56px"
              style={{
                objectFit: "cover",
                aspectRatio: "1 / 1",
                ...(needsDarkFilter ? { filter: "brightness(0) invert(1)" } : null),
              }}
              className="absolute inset-0 h-full w-full rounded-full ring-2 ring-emerald-400/40 shadow-glow transition-transform group-hover:scale-105 hidden dark:block"
            />
          )}
        </div>
      ) : (
        <div className="logo-mark relative h-12 w-12 md:h-14 md:w-14 rounded-full gradient-primary shadow-glow flex items-center justify-center text-primary-foreground font-bold text-xl transition-transform group-hover:scale-105 ring-2 ring-emerald-500/40">
          <span className="font-display">৳</span>
          <span className="absolute inset-0 rounded-full ring-1 ring-inset ring-white/30" />
        </div>
      )}
      <div className={`logo-text flex flex-col leading-tight min-w-0 ${sizeClasses.text}`}>
        <span
          className={`logo-title font-display font-bold tracking-tight whitespace-nowrap truncate ${
            variant === "footer"
              ? "!text-black dark:!text-black"
              : isLg
                ? ""
                : "text-emerald-500 dark:!text-white"
          } ${sizeClasses.name}`}
        >
          {siteName}
        </span>
        <span
          className={`logo-tag font-semibold whitespace-nowrap truncate ${
            variant === "footer"
              ? "!text-black dark:!text-black"
              : "text-emerald-600 dark:text-emerald-400"
          } ${sizeClasses.tag}`}
        >
          {tagline}
        </span>
      </div>
    </Link>
  );
}
